// /client/src/components/PodRelayManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const PodRelayManager = () => {
  const navigate = useNavigate();
  
  const [activeRelays, setActiveRelays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processing, setProcessing] = useState(null); // Track which item is being processed

  // Fetch pods with active relays
  const fetchActiveRelays = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch('/api/pod-relay/relays/active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch active relays');
      }

      const data = await response.json();
      setActiveRelays(data.relays || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveRelays();
  }, []);

  // Break relay for a single pod
  const breakPodRelay = async (podId, podName) => {
    if (!window.confirm(`Break relay for pod "${podName}"? This will stop message forwarding for this pod.`)) {
      return;
    }

    try {
      setProcessing(podId);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/pod-relay/stop/${podId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate relay');
      }

      setSuccessMessage(`Relay deactivated for "${podName}"`);
      fetchActiveRelays();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  };

  // Break relay for all pods in a deployment
  const breakDeploymentRelays = async (deploymentId, deploymentName, podCount) => {
    if (!window.confirm(`Break relay for ALL ${podCount} pod(s) in deployment "${deploymentName}"?`)) {
      return;
    }

    try {
      setProcessing(deploymentId);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/pod-relay/deployment/${deploymentId}/deactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate deployment relays');
      }

      const data = await response.json();
      setSuccessMessage(`Deactivated relay for ${data.deactivatedCount} pod(s) in "${deploymentName}"`);
      fetchActiveRelays();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  };

  // Break ALL active relays
  const breakAllRelays = async () => {
    const totalPods = activeRelays.length;
    if (!window.confirm(`⚠️ Break relay for ALL ${totalPods} pod(s)? This cannot be undone.`)) {
      return;
    }

    try {
      setProcessing('all');
      const token = localStorage.getItem('token');

      const response = await fetch('/api/pod-relay/relays/deactivate-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate all relays');
      }

      const data = await response.json();
      setSuccessMessage(`Deactivated relay for ${data.deactivatedCount} pod(s)`);
      fetchActiveRelays();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  };

  // Group relays by deployment
  const groupedByDeployment = activeRelays.reduce((acc, relay) => {
    const deploymentId = relay.deploymentId || 'no-deployment';
    const deploymentName = relay.deploymentName || 'No Deployment Linked';
    
    if (!acc[deploymentId]) {
      acc[deploymentId] = {
        deploymentId,
        deploymentName,
        deploymentStatus: relay.deploymentStatus,
        pods: []
      };
    }
    acc[deploymentId].pods.push(relay);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <p className="has-text-centered py-6">Loading active relays...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="theme-light m-0 p-0" data-theme="light">
      <div className='is-flex is-flex-direction-column is-justify-content-center'>
        <Navigation />

        <div className='mt-2 pt-6 container px-6'>
          <button 
            className="button is-small is-text mb-4"
            onClick={() => navigate('/pods')}
          >
            ← Back
          </button>

          <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
            <div>
              <h1 className='h1 my-2'>Pod Relay Manager</h1>
              <p className="has-text-grey">
                {activeRelays.length} pod{activeRelays.length !== 1 ? 's' : ''} with active relay
              </p>
            </div>
            <div className="buttons">
              <button 
                className="button is-light"
                onClick={fetchActiveRelays}
              >
                Refresh
              </button>
              {activeRelays.length > 0 && (
                <button 
                  className={`button is-danger ${processing === 'all' ? 'is-loading' : ''}`}
                  onClick={breakAllRelays}
                  disabled={processing}
                >
                  Break All Relays
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          {successMessage && (
            <div className="notification is-success">
              <button className="delete" onClick={() => setSuccessMessage('')}></button>
              {successMessage}
            </div>
          )}

          {error && (
            <div className="notification is-danger">
              <button className="delete" onClick={() => setError('')}></button>
              {error}
            </div>
          )}

          {/* No Active Relays */}
          {activeRelays.length === 0 ? (
            <div className="notification is-info is-light">
              <p><strong>No active pod relays.</strong></p>
              <p className="is-size-7 mt-2">
                Pod relays are activated when a deployment starts and deactivated when it completes or is cancelled.
              </p>
            </div>
          ) : (
            /* Grouped by Deployment */
            Object.values(groupedByDeployment).map((group) => (
              <div key={group.deploymentId} className="box mb-4">
                <div className="is-flex is-justify-content-space-between is-align-items-start mb-4">
                  <div>
                    <h2 className="subtitle is-5 mb-1">
                      {group.deploymentName}
                    </h2>
                    <div className="tags">
                      {group.deploymentStatus && (
                        <span className={`tag ${
                          group.deploymentStatus === 'in delivery' ? 'is-info' :
                          group.deploymentStatus === 'completed' ? 'is-success' :
                          group.deploymentStatus === 'cancelled' ? 'is-warning' :
                          'is-light'
                        }`}>
                          {group.deploymentStatus}
                        </span>
                      )}
                      <span className="tag is-light">
                        {group.pods.length} pod{group.pods.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  {group.deploymentId !== 'no-deployment' && (
                    <button
                      className={`button is-warning is-small ${processing === group.deploymentId ? 'is-loading' : ''}`}
                      onClick={() => breakDeploymentRelays(group.deploymentId, group.deploymentName, group.pods.length)}
                      disabled={processing}
                    >
                      Break All in Deployment
                    </button>
                  )}
                </div>

                <table className="table is-fullwidth is-narrow">
                  <thead>
                    <tr>
                      <th>Pod Name</th>
                      <th>Pod ID</th>
                      <th>Relay Started</th>
                      <th>Last Message</th>
                      <th>Members</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.pods.map((relay) => (
                      <tr key={relay.podId}>
                        <td>
                          <a 
                            onClick={() => navigate(`/pods/${relay.podId}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {relay.podName}
                          </a>
                        </td>
                        <td>
                          <code className="is-size-7">{relay.podIdCode}</code>
                        </td>
                        <td className="is-size-7">
                          {formatDate(relay.relayStartedAt)}
                        </td>
                        <td className="is-size-7">
                          {formatDate(relay.lastMessageAt)}
                        </td>
                        <td>
                          <span className="tag is-small">{relay.memberCount}</span>
                        </td>
                        <td className="has-text-right">
                          <button
                            className={`button is-small is-outlined is-danger ${processing === relay.podId ? 'is-loading' : ''}`}
                            onClick={() => breakPodRelay(relay.podId, relay.podName)}
                            disabled={processing}
                          >
                            Break
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}

          {/* Help Text */}
          <div className="content is-size-7 has-text-grey mt-5">
            <p><strong>What is a pod relay?</strong></p>
            <p>
              When a deployment is "in delivery", pod relay is activated. This means when a participant 
              replies to a message, their reply is forwarded to all other members in their pod.
            </p>
            <p>
              Relay automatically deactivates when a deployment completes or is cancelled. 
              Use this page to manually break relays if needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PodRelayManager;